import React, { useState, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { updateTeam } from 'store/auth'
import { buildTeamSlotMap } from 'utils/teamVisualIdentity'
import EditPencilIcon from 'assets/icons/edit-pencil.svg'
import HostBadgeIcon from 'assets/icons/host-badge.svg'
import RobotIcon from 'assets/icons/robot.svg'
import ActionButton from 'components/Shared/ActionButton'
import FacilitatorsBox from './FacilitatorsBox'
import {
  TEAM_SLOTS,
  MAX_PLAYERS_PER_TEAM,
  LOBBY_NAME_MAX_LENGTH,
  clampLobbyNameInput,
  getAvatarSrc,
  DEFAULT_AVATAR,
  AVATAR_NAMES,
  LOBBY_TEAM_COLORS,
  AI_STRATEGY_OPTIONS
} from './constants'

function TeamsSection({
  teams,
  email,
  currentTeamId,
  pendingTeamChoice,
  joiningTeamSlot,
  onChooseTeam,
  onAddAiTeam,
  addingAiSlot,
  canAddAi = true,
  isGameCreator,
  canObserveStartedGames,
  onFacilitatorEnterTeam,
  enteringTeamId,
  onRemoveAiTeam,
  removingAiTeamId,
  facilitators,
  onFacilitatorLeaveTeam,
  gameWaitingForPlayers,
  isFacilitatorSession
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [editingTeamId, setEditingTeamId] = useState(null)
  const [editTeamNameValue, setEditTeamNameValue] = useState('')
  const [savingTeamName, setSavingTeamName] = useState(false)

  const handleSaveTeamName = useCallback(async () => {
    const name = editTeamNameValue.trim()
    if (!name || name.length > LOBBY_NAME_MAX_LENGTH) return
    setSavingTeamName(true)
    try {
      await dispatch(updateTeam({ name }))
      setEditingTeamId(null)
    } catch (err) {
      const msg =
        err.response?.data?.message || err.response?.data?.errors?.[0] || t('lobby.start_error')
      toast.error(msg)
    } finally {
      setSavingTeamName(false)
    }
  }, [dispatch, editTeamNameValue, t])

  const teamSlotMapRef = useRef(new Map())

  const teamSlotMap = useMemo(() => {
    const prev = teamSlotMapRef.current
    const map = buildTeamSlotMap(teams, prev)
    teamSlotMapRef.current = map
    return map
  }, [teams])

  const hasAnyAiTeam = useMemo(
    () => teams.some((tm) => tm.ai || !!tm.decision_strategy_name),
    [teams]
  )

  const findTeamBySlot = (slot) =>
    teams.find((tm) => teamSlotMap.get(String(tm.id)) === slot) || null

  const getTeamColor = (slotIndex) => LOBBY_TEAM_COLORS[slotIndex % LOBBY_TEAM_COLORS.length]

  const getAiStrategyLabel = (team) => {
    const stratName = team?.decision_strategy_name || ''
    return `AI Player (${stratName || 'AI'})`
  }

  return (
    <div className="lobby__teams-section">
      <div className="lobby__teams-grid">
        {TEAM_SLOTS.map((slot, slotIndex) => {
          const team = findTeamBySlot(slot)
          const displayName = team?.name || `Team ${slot}`
          const isAiTeam = team?.ai || !!team?.decision_strategy_name
          const isCurrentSeatTeam = team?.id === currentTeamId
          const playerCount = (team?.players || []).length
          const isFull = playerCount >= MAX_PLAYERS_PER_TEAM
          const isEmptySlot = !team || playerCount === 0
          const canEditTeamName = team?.id === currentTeamId && !isAiTeam
          const isEditingThisTeam = editingTeamId === team?.id
          const hasLobbySeat = Boolean(currentTeamId)
          const canCreateLobbySeat =
            !hasLobbySeat &&
            (pendingTeamChoice || canObserveStartedGames || (isFacilitatorSession && gameWaitingForPlayers))
          const showEnterAsFacilitator = canObserveStartedGames && team && !gameWaitingForPlayers
          const showFacilitatorLeave =
            isFacilitatorSession && gameWaitingForPlayers && isCurrentSeatTeam
          const showJoinTeam =
            gameWaitingForPlayers &&
            !hasAnyAiTeam &&
            !isAiTeam &&
            (!isFull || isFacilitatorSession) &&
            !isCurrentSeatTeam &&
            (hasLobbySeat || canCreateLobbySeat)
          const showAddAi = isGameCreator && isEmptySlot && !isAiTeam && !pendingTeamChoice
          const teamColor = getTeamColor(slotIndex)

          return (
            <div key={slot} className="lobby__team-slot">
              <div className="lobby__team-slot-header">
                <div className="lobby__team-slot-title">
                  {!isEditingThisTeam ? (
                    <>
                      <span
                        className="lobby__team-slot-name"
                        style={{ backgroundColor: teamColor }}
                      >
                        {displayName}
                      </span>
                      {canEditTeamName && (
                        <button
                          type="button"
                          className="lobby__edit-icon-btn"
                          onClick={() => {
                            setEditTeamNameValue(displayName)
                            setEditingTeamId(team.id)
                          }}
                          aria-label={t('lobby.edit')}
                        >
                          <img src={EditPencilIcon} alt="" className="lobby__edit-icon-btn-img" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="lobby__game-edit-row lobby__game-edit-row--team">
                      <input
                        type="text"
                        className="lobby__form-input lobby__team-name-input"
                        value={editTeamNameValue}
                        maxLength={LOBBY_NAME_MAX_LENGTH}
                        onChange={(e) =>
                          setEditTeamNameValue(
                            clampLobbyNameInput(editTeamNameValue, e.target.value)
                          )
                        }
                        placeholder={t('lobby.team_name')}
                      />
                      <button
                        type="button"
                        className="lobby__save-icon-btn"
                        onClick={() => handleSaveTeamName(team.id)}
                        disabled={
                          savingTeamName ||
                          !editTeamNameValue.trim() ||
                          editTeamNameValue.trim().length > LOBBY_NAME_MAX_LENGTH
                        }
                        aria-label={t('lobby.save')}
                      >
                        <i className="fa-solid fa-check" />
                      </button>
                    </div>
                  )}
                </div>
                {!isEditingThisTeam &&
                  (isFull || isAiTeam ? (
                    <span className="lobby__team-full-badge">{t('lobby.full')}</span>
                  ) : (
                    <span className="lobby__team-slot-count">
                      {playerCount}/{MAX_PLAYERS_PER_TEAM}
                    </span>
                  ))}
              </div>

              {/* eslint-disable-next-line no-nested-ternary */}
              {isAiTeam ? (
                <ul className="lobby__team-players">
                  <li className="lobby__team-player lobby__team-player--ai">
                    <div className="lobby__ai-icon">
                      <img src={RobotIcon} alt="" className="lobby__ai-icon-img" />
                    </div>
                    <span>{getAiStrategyLabel(team)}</span>
                  </li>
                </ul>
              ) : team && (team.players || []).length > 0 ? (
                <ul className="lobby__team-players">
                  {(team.players || []).map((player) => {
                    const isCurrentPlayer =
                      player.email && email && player.email.toLowerCase() === email.toLowerCase()
                    return (
                      <li
                        key={player.id}
                        className={`lobby__team-player${
                          isCurrentPlayer ? ' lobby__team-player--current' : ''
                        }`}
                      >
                        <div className="lobby__team-player-avatar-wrapper">
                          <div
                            className="lobby__team-player-avatar"
                            style={team.lobby_ready ? { borderColor: teamColor } : undefined}
                          >
                            <img
                              src={getAvatarSrc(
                                player?.avatar && AVATAR_NAMES.includes(player.avatar)
                                  ? player.avatar
                                  : DEFAULT_AVATAR
                              )}
                              alt=""
                              className="lobby__avatar-img"
                            />
                          </div>
                          <span
                            className={`lobby__player-status-badge${
                              team.lobby_ready
                                ? ' lobby__player-status-badge--ready'
                                : ' lobby__player-status-badge--not-ready'
                            }`}
                            style={team.lobby_ready ? { backgroundColor: teamColor } : undefined}
                            aria-hidden
                          >
                            <i
                              className={
                                team.lobby_ready ? 'fa-solid fa-check' : 'fa-solid fa-ellipsis'
                              }
                            />
                          </span>
                        </div>
                        <span className="lobby__team-player-name">
                          {player.alias || player.email}
                          {isCurrentPlayer && isGameCreator && (
                            <img
                              src={HostBadgeIcon}
                              alt=""
                              className="lobby__host-badge"
                              title="Host"
                            />
                          )}
                        </span>
                        {player.game_role && (
                          <span className="lobby__team-player-role">
                            {player.game_role.toUpperCase()}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="lobby__team-no-players">{t('lobby.waiting_for_participants')}</p>
              )}

              {showEnterAsFacilitator && (
                <button
                  type="button"
                  className="lobby__facilitator-enter-btn"
                  onClick={() => onFacilitatorEnterTeam(team.id)}
                  disabled={enteringTeamId !== null}
                >
                  {enteringTeamId === team.id ? t('lobby.entering') : t('lobby.spectate_team')}
                </button>
              )}

              {showFacilitatorLeave && onFacilitatorLeaveTeam && (
                <button
                  type="button"
                  className="lobby__remove-ai-btn"
                  onClick={() => onFacilitatorLeaveTeam()}
                >
                  {t('lobby.leave_team')}
                </button>
              )}

              {showJoinTeam && onChooseTeam && (
                <ActionButton
                  variant="secondary"
                  className="lobby__join-team-btn"
                  handleOnClick={() => onChooseTeam(team?.id || null, slot)}
                  disabled={joiningTeamSlot !== null}
                >
                  {joiningTeamSlot === slot ? t('lobby.joining') : t('lobby.join_team')}
                </ActionButton>
              )}

              {isAiTeam && isGameCreator && onRemoveAiTeam && (
                <button
                  type="button"
                  className="lobby__remove-ai-btn"
                  onClick={() => onRemoveAiTeam(team.id)}
                  disabled={removingAiTeamId !== null}
                >
                  {removingAiTeamId === team.id ? t('lobby.loading') : t('lobby.remove_ai')}
                </button>
              )}

              {showAddAi && canAddAi && (
                <select
                  className="lobby__add-ai-select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      onAddAiTeam(slot, e.target.value)
                    }
                  }}
                  disabled={addingAiSlot !== null}
                >
                  <option value="">
                    {addingAiSlot === slot ? t('lobby.adding_ai') : t('lobby.add_ai_team')}
                  </option>
                  {AI_STRATEGY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}

              {!team && !showJoinTeam && pendingTeamChoice && joiningTeamSlot === slot && (
                <p className="lobby__team-slot-joining">{t('lobby.joining')}</p>
              )}
            </div>
          )
        })}
        <FacilitatorsBox facilitators={facilitators} teams={teams} />
      </div>
    </div>
  )
}

TeamsSection.propTypes = {
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      color: PropTypes.string,
      lobby_ready: PropTypes.bool,
      ai: PropTypes.bool,
      decision_strategy_name: PropTypes.string,
      players: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          alias: PropTypes.string,
          email: PropTypes.string,
          game_role: PropTypes.string,
          avatar: PropTypes.string
        })
      )
    })
  ).isRequired,
  email: PropTypes.string,
  currentTeamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pendingTeamChoice: PropTypes.bool,
  joiningTeamSlot: PropTypes.number,
  onChooseTeam: PropTypes.func,
  onAddAiTeam: PropTypes.func,
  addingAiSlot: PropTypes.number,
  canAddAi: PropTypes.bool,
  isGameCreator: PropTypes.bool,
  canObserveStartedGames: PropTypes.bool,
  onFacilitatorEnterTeam: PropTypes.func,
  enteringTeamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onRemoveAiTeam: PropTypes.func,
  removingAiTeamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  facilitators: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
  onFacilitatorLeaveTeam: PropTypes.func,
  gameWaitingForPlayers: PropTypes.bool,
  isFacilitatorSession: PropTypes.bool
}

TeamsSection.defaultProps = {
  email: null,
  currentTeamId: null,
  pendingTeamChoice: false,
  joiningTeamSlot: null,
  onChooseTeam: null,
  onAddAiTeam: null,
  addingAiSlot: null,
  canAddAi: true,
  isGameCreator: false,
  canObserveStartedGames: false,
  onFacilitatorEnterTeam: null,
  enteringTeamId: null,
  onRemoveAiTeam: null,
  removingAiTeamId: null,
  facilitators: [],
  onFacilitatorLeaveTeam: null,
  gameWaitingForPlayers: true,
  isFacilitatorSession: false
}

export default TeamsSection
